import React, { useCallback, useEffect, useState } from "react";

import PlaceHolderImage from "./Components/PlaceHolderImage";
import RequestPermissionCard from "./Components/RequestCard";

import ImageGrid from "@/Components/Media/User/Layout/UserImageGrid";
import { type AppUserSession } from "@/schemas/auth/session";
import { type UserProfile } from "@/schemas/user/profile";
import MediaService from "@/services/user/media";
import {
  UserImageType,
  type UserLikableImageMedia,
} from "@/schemas/user/image";
import { RequestState } from "@/services/user/permission";
import UserImageActionableCard from "@/Components/Media/User/Images/UserImageActionableCard";

type PrivatePhotoGridProps = {
  session: AppUserSession;
  profile: UserProfile;
};

const createPlaceholderImage = (index: number): UserLikableImageMedia => ({
  isLiked: false,
  image: {
    id: `placeholder-${index}`,
    likeCount: 0,
    username: "",
    type: UserImageType.PRIVATE_IMAGE,
    attributes: {
      url: "",
      previewUrl: "",
      width: 0,
      height: 0,
    },
  },
});

function PrivatePhotoTab({
  session,
  profile,
}: PrivatePhotoGridProps): React.JSX.Element {
  const mediaService = new MediaService(session);
  const [isLoading, setIsLoading] = useState(false);
  const [photos, setPhotos] = useState<UserLikableImageMedia[]>([]);
  const isOwner = session.identity.username === profile.username;
  const hasAccess =
    profile.privatePhotoAccess.status === RequestState.APPROVED || isOwner;

  const renderImage = useCallback(
    ({ item }: { item: UserLikableImageMedia | null }): React.JSX.Element => {
      if (item === null) {
        return (
          <RequestPermissionCard
            session={session}
            profile={profile}
            bgColors={[]}
            numColumns={2}
          />
        );
      }

      if (!hasAccess) {
        return <PlaceHolderImage bgColors={[]} numColumns={2} />;
      }

      return (
        <UserImageActionableCard
          userLikableImage={item}
          session={session}
          numColumns={2}
          isReadOnly={true}
          isPixelated={false}
        />
      );
    },
    [],
  );

  const fetchPhotos = async (): Promise<void> => {
    try {
      setIsLoading(true);
      if (hasAccess) {
        const imageResponse = await mediaService.getImages(
          profile.username,
          UserImageType.PRIVATE_IMAGE,
          { limit: 20, next: null },
        );
        setPhotos(imageResponse.items);
      } else {
        const placeholders: UserLikableImageMedia[] = Array.from(
          { length: profile.privatePhotoCount },
          (_, index) => createPlaceholderImage(index),
        );
        setPhotos(placeholders);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onLoadMorePhotos = async (): Promise<void> => {
    // TODO; Implement pagination
  };

  useEffect(() => {
    fetchPhotos();
  }, []);

  return (
    <ImageGrid
      images={hasAccess ? photos : [null, ...photos]}
      isLoading={isLoading}
      isLoadingMore={false}
      onLoadMore={onLoadMorePhotos}
      onRefresh={fetchPhotos}
      renderImage={renderImage}
    />
  );
}

export default PrivatePhotoTab;
